import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { KeyResult } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUpdateKeyResult } from "@/hooks/useKeyResults";
import { useToast } from "@/hooks/use-toast";
import { okrErrorDescription } from "@/lib/okr-errors";

interface KrProgressPopoverProps {
    keyResult: KeyResult;
    children: ReactNode;
}

/** Inline progress editor for a KR row; the objective rollup recomputes on the BE after save. */
export function KrProgressPopover({ keyResult, children }: KrProgressPopoverProps) {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(String(keyResult.progress));
    const { mutate, isPending } = useUpdateKeyResult();
    const { toast } = useToast();
    const inputId = useId();

    const save = () => {
        if (isPending) return;
        const progress = value.trim() === "" ? NaN : Number(value);
        if (!Number.isFinite(progress) || progress < 0 || progress > keyResult.targetProgress) {
            toast({
                title: "Invalid progress",
                description: `Enter a number between 0 and ${keyResult.targetProgress}.`,
                variant: "destructive",
            });
            return;
        }
        mutate({ pid: keyResult.pid, data: { progress } }, {
            onSuccess: () => {
                toast({ title: "Progress updated" });
                setOpen(false);
            },
            onError: (error) => {
                toast({ title: "Error", description: okrErrorDescription(error, "Could not update progress. Please try again."), variant: "destructive" });
            },
        });
    };

    return (
        <Popover open={open} onOpenChange={next => { setOpen(next); if (next) setValue(String(keyResult.progress)); }}>
            <PopoverTrigger asChild onClick={e => e.stopPropagation()}>{children}</PopoverTrigger>
            <PopoverContent className="w-56 p-3" onClick={e => e.stopPropagation()}>
                <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Progress (0–{keyResult.targetProgress})
                </label>
                <div className="flex items-center gap-2">
                    <Input
                        id={inputId}
                        type="number"
                        disabled={isPending}
                        min={0}
                        max={keyResult.targetProgress}
                        step={1}
                        value={value}
                        autoFocus
                        onChange={e => setValue(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") { e.preventDefault(); save(); }
                            if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
                        }}
                        className="h-8 text-sm"
                    />
                    <Button size="sm" className="h-8 shrink-0" onClick={save} disabled={isPending}>
                        Save
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
